import L from 'leaflet'
import dayjs from 'dayjs'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

// 常量定义
const TERMINATOR_UPDATE_INTERVAL = 300000 // 5分钟 (毫秒)
const LONGITUDE_STEP = 1 // 经度步长
const DAYS_IN_YEAR = 365
const EARTH_TILT = 23.44
const HOURS_IN_DAY = 24
const DEG_TO_RAD = Math.PI / 180
const RAD_TO_DEG = 180 / Math.PI
const TIME_BUTTONS = [-18, -12, -6, -1, 0, +1, +6, +12, +18]
const DAYS_OF_WEEK = ['日', '一', '二', '三', '四', '五', '六']

// 太阳位置接口
interface SunPosition {
  altitude: number
  azimuth: number
  latitude: number
  longitude: number
}

class Terminator {
  private hourOffset = 0
  private updateIntervalId: number | null = null
  private clockTimerId: number | null = null
  private isInitialized = false
  private layerGroup: L.LayerGroup | null = null
  private readonly map: MyMap

  constructor(map: MyMap) {
    this.map = map
  }

  /**
   * 计算太阳赤纬（太阳直射点的纬度）
   * @param dayOfYear 一年中的第几天 (0-364)
   * @returns 太阳赤纬（角度）
   */
  private calculateSolarDeclination(dayOfYear: number): number {
    return (
      EARTH_TILT *
      Math.sin((360 / DAYS_IN_YEAR) * (dayOfYear - 81) * DEG_TO_RAD)
    )
  }

  /**
   * 计算太阳时角
   * @param date UTC日期对象
   * @returns 太阳时角（角度）
   */
  private calculateSolarHourAngle(date: Date): number {
    const utcHours =
      date.getUTCHours() +
      date.getUTCMinutes() / 60 +
      date.getUTCSeconds() / 3600
    return (utcHours - 12) * 15
  }

  /**
   * 计算晨昏线纬度
   * @param longitude 经度
   * @param solarDeclination 太阳赤纬
   * @param solarHourAngle 太阳时角
   * @returns 晨昏线纬度
   */
  private calculateTerminatorLatitude(
    longitude: number,
    solarDeclination: number,
    solarHourAngle: number
  ): number {
    const terminatorLat =
      Math.atan(
        -Math.cos((longitude + solarHourAngle) * DEG_TO_RAD) /
          Math.tan(solarDeclination * DEG_TO_RAD)
      ) * RAD_TO_DEG

    return terminatorLat
  }

  /**
   * 清除所有图层和定时器
   */
  private clearResources(): void {
    try {
      this.isInitialized = false

      // 清除图层
      if (this.layerGroup) {
        this.layerGroup.clearLayers()
        this.map.removeLayer(this.layerGroup)
        this.layerGroup = null
      }

      // 清除定时器
      if (this.updateIntervalId) {
        clearInterval(this.updateIntervalId)
        this.updateIntervalId = null
      }

      if (this.clockTimerId) {
        clearInterval(this.clockTimerId)
        this.clockTimerId = null
      }

      this.hourOffset = 0
    } catch (error) {
      console.error('清除资源时出错:', error)
    }
  }

  /**
   * 初始化UI组件
   */
  private initializeUI(): void {
    if (this.isInitialized) return

    try {
      // 创建图层组
      this.layerGroup = L.layerGroup().addTo(this.map)

      // 生成时间按钮HTML
      const hourButtons = TIME_BUTTONS.map((hour) => {
        const formattedHour = hour > 0 ? `+${hour}` : hour.toString()
        return hour === 0
          ? `<span data-hour="${hour}" class="on">现在（北京时间）</span>`
          : `<span data-hour="${hour}">${formattedHour}小时</span>`
      }).join('')

      const html = `
        <div id='terminatorcontainer' class='terminatorcontainer'>
          <div class='terminatorbox'>
            <div><label>UTC+8 时间: </label><label id='utc-time'></label></div>
            <div class='time-buttons'>${hourButtons}</div>
            <div class='terminator-close'></div>
          </div>
        </div>
      `

      // 避免重复插入HTML
      if (!document.querySelector('#terminatorcontainer')) {
        document.body.insertAdjacentHTML('beforeend', html)
      }

      // 绑定按钮事件
      document.querySelectorAll('.time-buttons span').forEach((span) => {
        span.addEventListener('click', () => {
          document.querySelectorAll('.time-buttons span').forEach((sibling) => {
            sibling.classList.remove('on')
          })

          span.classList.add('on')
          const hour = Number.parseInt(
            (span as HTMLElement).dataset.hour || '0'
          )
          this.showTerminator(hour)
        })
      })

      // 绑定关闭按钮事件
      document
        .querySelector('.terminator-close')
        ?.addEventListener('click', () => {
          this.hideTerminatorUI()
        })

      // 更新时间显示
      this.updateClockDisplay()
      this.clockTimerId = window.setInterval(
        () => this.updateClockDisplay(),
        1000
      )

      this.isInitialized = true
    } catch (error) {
      console.error('初始化UI失败:', error)
    }
  }

  /**
   * 显示晨昏线
   * @param hourOffset 小时偏移量
   */
  private showTerminator(hourOffset = 0): void {
    console.info(this.layerGroup)
    if (!this.layerGroup) return
    this.layerGroup.clearLayers()
    this.hourOffset = hourOffset
    const now = new Date(Date.now() + (hourOffset || 0) * 3600 * 1e3)
    const coords = this.generateTerminator(now)
    const maxCoords = this.getMaxFormTerminatorCoords(coords)

    const cycles = [-360, 0, 360]
    const sunLat = this.getSolarDeclination(new Date(now))
    const sunLng = this.getSolarLongitude(new Date(now))

    cycles.forEach((offset) => {
      const offsetCoords = coords.map((coord: any) => [
        coord[0],
        coord[1] + offset,
      ])
      const outer = maxCoords.map((coord: any) => [coord[0], coord[1] + offset])

      // 创建晨昏线多边形
      this.layerGroup?.addLayer(
        L.polygon([outer, offsetCoords], {
          color: '#ff9900',
          weight: 2,
          fillColor: '#333',
          fillOpacity: 0.3,
          interactive: false,
        })
      )

      // 添加太阳位置标记
      if (this.layerGroup) {
        this.layerGroup.addLayer(
          L.marker([sunLat, sunLng + offset], {
            icon: L.icon({
              iconUrl: 'https://www.shipxy.com/Content/images/sun-pos.png',
              iconSize: [45, 45],
              iconAnchor: [22, 22],
            }),
            interactive: false,
          })
        )
      }
    })
  }
  private getSolarDeclination(date: Date): number {
    const dayOfYear = Math.floor(
      (date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) /
        (1000 * 60 * 60 * HOURS_IN_DAY)
    )
    return this.calculateSolarDeclination(dayOfYear)
  }

  private getSolarLongitude(date: Date): number {
    const dayOfYear = Math.floor(
      (date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) /
        (1000 * 60 * 60 * HOURS_IN_DAY)
    )
    const solarDeclination = this.calculateSolarDeclination(dayOfYear)
    const solarHourAngle = this.calculateSolarHourAngle(date)

    let minLat = 90
    let minLng = -180
    for (let lon = -180; lon <= 180; lon += LONGITUDE_STEP) {
      const lat = this.calculateTerminatorLatitude(
        lon,
        solarDeclination,
        solarHourAngle
      )
      if (lat < minLat) {
        minLat = lat
        minLng = lon
      }
    }
    return minLng
  }
  private generateTerminator(date: Date): any[] {
    const dayOfYear = Math.floor(
      (date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) /
        (1000 * 60 * 60 * HOURS_IN_DAY)
    )
    const solarDeclination = this.calculateSolarDeclination(dayOfYear)
    const solarHourAngle = this.calculateSolarHourAngle(date)

    const points: any[] = []
    for (let lon = -180; lon <= 180; lon += 1) {
      const lat = this.calculateTerminatorLatitude(
        lon,
        solarDeclination,
        solarHourAngle
      )
      points.push([lat, lon])
    }

    if (this.isSunAtTropic(date) >= 0) points.push([-90, 180], [-90, -180])
    else points.push([90, 180], [90, -180])

    return points
  }

  private isSunAtTropic(date: Date): number {
    const dayOfYear = Math.floor(
      (date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) /
        (1000 * 60 * 60 * HOURS_IN_DAY)
    )
    const solarDeclination = this.calculateSolarDeclination(dayOfYear)
    if (solarDeclination > 0) {
      return -1
    } else if (solarDeclination < 0) {
      return 1
    } else {
      return 0
    }
  }

  private getMaxFormTerminatorCoords(terminatorCoords: any[]): any[] {
    let latMax = -90
    let latMin = 90
    let lngMax = -180
    let lngMin = 180
    terminatorCoords.forEach((c: any) => {
      if (c[0] > latMax) latMax = c[0]
      if (c[0] < latMin) latMin = c[0]
      if (c[1] > lngMax) lngMax = c[1]
      if (c[1] < lngMin) lngMin = c[1]
    })

    return latMin === -90
      ? [
          [latMin, lngMin],
          [latMin, lngMax],
          [90, 180],
          [90, -180],
        ]
      : [
          [-90, -180],
          [-90, 180],
          [latMax, lngMax],
          [latMax, lngMin],
        ]
  }

  /**
   * 隐藏晨昏线UI
   */
  private hideTerminatorUI(): void {
    const container = document.querySelector('#terminatorcontainer')
    if (container) {
      ;(container as HTMLElement).style.display = 'none'
    }
  }

  /**
   * 更新时钟显示
   */
  private updateClockDisplay(): void {
    try {
      const timeElement = document.querySelector('#utc-time')
      if (timeElement) {
        const now = dayjs()
        const dayOfWeek = DAYS_OF_WEEK[now.day()]
        timeElement.textContent = `${now.format(
          'YYYY-MM-DD HH:mm:ss'
        )}，星期${dayOfWeek}`
      }
    } catch (error) {
      console.error('更新时间显示失败:', error)
    }
  }

  /**
   * 切换晨昏线显示状态
   * @param event 触发事件的元素
   */
  toggleVisibility(isShow = false): void {
    try {
      const container = document.querySelector('#terminatorcontainer')
      if (!isShow) {
        this.clearResources()
        if (container) (container as HTMLElement).style.display = 'none'
      } else {
        this.initializeUI()
        this.showTerminator()
        if (container) (container as HTMLElement).style.display = 'block'

        // 设置定期更新
        this.updateIntervalId = window.setInterval(() => {
          this.showTerminator(this.hourOffset)
        }, TERMINATOR_UPDATE_INTERVAL)
        this.map.setZoom(2)
      }
    } catch (error) {
      console.error('切换显示状态失败:', error)
    }
  }
}

/**
 * 创建晨昏线控制器
 * @param map 地图实例
 * @returns Terminator 实例
 */
const terminatorSymbol = (map: MyMap): Terminator => new Terminator(map)

export { terminatorSymbol, Terminator }
